import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
} from "react-router-dom";
import { getAuthWrapper, loadCookiesData, getReadyAuthUser } from "~/utils";
import dayjs from "dayjs";
import {
  apiClient,
  HomePageAnalyticsLoaderResult,
  HomePageOperationsLoaderResult,
} from "~/services/apiClient";
import { ExpiringLeasesResult } from "~/types/finance";
import { emptyExpiringLeasesResult } from "~/utils/finance/expiringLeases";
import * as APITypes from "~/API";

const emptyAnalytics = (): HomePageAnalyticsLoaderResult => ({
  monthlyIncome: 0,
  yearlyIncome: 0,
  monthlyExpenses: 0,
  yearlyExpenses: 0,
  monthlyNet: 0,
  yearlyNet: 0,
});

const emptyOperations = (): HomePageOperationsType => ({
  unitCount: 0,
  offMarketCount: 0,
  occupiedCount: 0,
  vacancyCount: 0,
  inRenovationCount: 0,
  inProgressServiceRequests: 0,
  expiringLeases: emptyExpiringLeasesResult(),
  rentNeverPaidCount: 0,
  rentInProgressCount: 0,
  rentNeverPaidBalanceDue: 0,
  rentInProgressBalanceDue: 0,
  rentCollectionMonth: dayjs().month(),
  rentCollectionYear: dayjs().year(),
  leasesAwaitingReviewCount: 0,
  pendingAmenityReservationsCount: 0,
  pendingTransactionsCount: 0,
  hasShortTermStayInventory: false,
  pendingStaysCount: 0,
  arrivalsTodayCount: 0,
  departuresTodayCount: 0,
  noShowCount: 0,
});

function resolvePeriodFromRequest(request: Request) {
  const { searchParams } = new URL(request.url);
  const now = dayjs();
  const yearParam = searchParams.get("year");
  const monthParam = searchParams.get("month");
  const expiringWindowsDaysParam = searchParams.get("expiringWindowsDays");
  const parsedYear = yearParam == null ? NaN : Number(yearParam);
  const parsedMonth = monthParam == null ? NaN : Number(monthParam);
  const displayYear = Number.isFinite(parsedYear) ? parsedYear : now.year();
  const displayMonth = Number.isFinite(parsedMonth) ? parsedMonth : now.month();

  return {
    displayYear,
    displayMonth,
    expiringWindowsDaysParam,
  };
}

async function loadHomeOperations(
  ownerID: string,
  ownerType: APITypes.OwnerType,
  userID: string,
  displayYear: number,
  displayMonth: number,
  expiringWindowsDaysParam: string | null,
): Promise<HomePageOperationsType> {
  const result = await apiClient.loader<HomePageOperationsLoaderResult>(
    "home-page-operations",
    {
      ownerID,
      ownerType,
      userID,
      year: String(displayYear),
      month: String(displayMonth),
      ...(expiringWindowsDaysParam
        ? { expiringWindowsDays: expiringWindowsDaysParam }
        : {}),
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load home operations:", result.error);
    throw new Error(result.error ?? "Failed to load home operations");
  }

  const data = result.data;

  return {
    unitCount: data.unitCount,
    offMarketCount: data.offMarketCount,
    occupiedCount: data.occupiedCount,
    vacancyCount: data.vacancyCount,
    inRenovationCount: data.inRenovationCount,
    inProgressServiceRequests: data.inProgressServiceRequests,
    expiringLeases: data.expiringLeases ?? emptyExpiringLeasesResult(),
    rentNeverPaidCount: data.rentNeverPaidCount ?? 0,
    rentInProgressCount: data.rentInProgressCount ?? 0,
    rentNeverPaidBalanceDue: data.rentNeverPaidBalanceDue ?? 0,
    rentInProgressBalanceDue: data.rentInProgressBalanceDue ?? 0,
    rentCollectionMonth: data.rentCollectionMonth ?? dayjs().month(),
    rentCollectionYear: data.rentCollectionYear ?? dayjs().year(),
    leasesAwaitingReviewCount: data.leasesAwaitingReviewCount ?? 0,
    pendingAmenityReservationsCount: data.pendingAmenityReservationsCount ?? 0,
    pendingTransactionsCount: data.pendingTransactionsCount ?? 0,
    hasShortTermStayInventory: data.hasShortTermStayInventory ?? false,
    pendingStaysCount: data.pendingStaysCount ?? 0,
    arrivalsTodayCount: data.arrivalsTodayCount ?? 0,
    departuresTodayCount: data.departuresTodayCount ?? 0,
    noShowCount: data.noShowCount ?? 0,
  };
}

async function loadHomeAnalytics(
  ownerID: string,
  ownerType: APITypes.OwnerType,
  userID: string,
  displayYear: number,
  displayMonth: number,
): Promise<HomePageAnalyticsType> {
  const result = await apiClient.loader<HomePageAnalyticsLoaderResult>(
    "home-page-analytics",
    {
      ownerID,
      ownerType,
      userID,
      year: String(displayYear),
      month: String(displayMonth),
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load home analytics:", result.error);
    throw new Error(result.error ?? "Failed to load home analytics");
  }

  return {
    monthlyIncome: result.data.monthlyIncome,
    yearlyIncome: result.data.yearlyIncome,
    monthlyExpenses: result.data.monthlyExpenses,
    yearlyExpenses: result.data.yearlyExpenses,
    monthlyNet: result.data.monthlyNet,
    yearlyNet: result.data.yearlyNet,
  };
}

export async function HomePageLoader({ request }: LoaderFunctionArgs) {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();

  if (!user?.uid) {
    return {
      operations: emptyOperations(),
      analytics: Promise.resolve(emptyAnalytics()),
      isAuthenticated: false,
    };
  }

  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) {
    return {
      operations: emptyOperations(),
      analytics: Promise.resolve(emptyAnalytics()),
      isAuthenticated: true,
    };
  }

  const { ownerID, ownerType } = authAccessInfo.data;
  const { displayYear, displayMonth, expiringWindowsDaysParam } =
    resolvePeriodFromRequest(request);

  const operations = await loadHomeOperations(
    ownerID,
    ownerType,
    user.uid,
    displayYear,
    displayMonth,
    expiringWindowsDaysParam,
  );

  return {
    operations,
    analytics: loadHomeAnalytics(
      ownerID,
      ownerType,
      user.uid,
      displayYear,
      displayMonth,
    ),
    isAuthenticated: true,
  };
}

export type HomePageOperationsType = {
  unitCount: number;
  offMarketCount: number;
  occupiedCount: number;
  vacancyCount: number;
  inRenovationCount: number;
  inProgressServiceRequests: number;
  expiringLeases: ExpiringLeasesResult;
  rentNeverPaidCount: number;
  rentInProgressCount: number;
  rentNeverPaidBalanceDue: number;
  rentInProgressBalanceDue: number;
  rentCollectionMonth: number;
  rentCollectionYear: number;
  leasesAwaitingReviewCount: number;
  pendingAmenityReservationsCount: number;
  pendingTransactionsCount: number;
  hasShortTermStayInventory: boolean;
  pendingStaysCount: number;
  arrivalsTodayCount: number;
  departuresTodayCount: number;
  noShowCount: number;
};

export type HomePageAnalyticsType = {
  monthlyIncome: number;
  yearlyIncome: number;
  monthlyExpenses: number;
  yearlyExpenses: number;
  monthlyNet: number;
  yearlyNet: number;
};

export type HomePageLoaderType = {
  operations: HomePageOperationsType;
  analytics: Promise<HomePageAnalyticsType>;
  isAuthenticated: boolean;
};

export async function HomePageAction({ request }: ActionFunctionArgs) {
  await request.formData();
  return {};
}
